/**
 * Fall 2026 Canada DST (ME-502703)
 *
 * Several Canadian regions adopted permanent DST in 2026, each landing in a
 * different tzdata release. A browser may have correct rules for some zones and
 * stale rules for others, so we classify each zone individually by formatting a
 * fixed post-change instant and reading the offset the browser's Intl reports.
 */

/** A UTC instant after every affected zone's rule change took effect. */
export const TZDB_PROBE_DATE_ISO = '2026-12-01T00:00:00Z';

type TzdbRelease = '2026b' | '2026c' | '2026d' | '2026e';

export type TzdbVerdict = 'new' | 'old' | 'unknown';

export interface TzdbZoneExpectation {
  /** tzdata release that introduced the new rules for this zone */
  release: TzdbRelease;
  /** Expected UTC offset (minutes) on the probe date with the new ruleset */
  newOffsetMinutes: number;
  /** Expected UTC offset (minutes) on the probe date with the stale ruleset */
  oldOffsetMinutes: number;
  /** Legacy alias zones are probed but excluded from the summary level */
  isAlias?: boolean;
}

/** Ordered from oldest to newest tzdata release. */
const TZDB_RELEASES: readonly TzdbRelease[] = [
  '2026b',
  '2026c',
  '2026d',
  '2026e',
];

/** Expected offsets per affected zone on {@link TZDB_PROBE_DATE_ISO}. */
export const TZDB_AFFECTED_ZONES = {
  'America/Vancouver': {
    release: '2026b',
    newOffsetMinutes: -7 * 60,
    oldOffsetMinutes: -8 * 60,
  },
  'America/Edmonton': {
    release: '2026c',
    newOffsetMinutes: -6 * 60,
    oldOffsetMinutes: -7 * 60,
  },
  'America/Yellowknife': {
    release: '2026c',
    newOffsetMinutes: -6 * 60,
    oldOffsetMinutes: -7 * 60,
  },
  'America/Inuvik': {
    release: '2026d',
    newOffsetMinutes: -6 * 60,
    oldOffsetMinutes: -7 * 60,
  },
  'America/Winnipeg': {
    release: '2026e',
    newOffsetMinutes: -5 * 60,
    oldOffsetMinutes: -6 * 60,
  },
  'America/Rainy_River': {
    release: '2026e',
    newOffsetMinutes: -5 * 60,
    oldOffsetMinutes: -6 * 60,
  },
  'Canada/Pacific': {
    release: '2026b',
    newOffsetMinutes: -7 * 60,
    oldOffsetMinutes: -8 * 60,
    isAlias: true,
  },
  'Canada/Mountain': {
    release: '2026c',
    newOffsetMinutes: -6 * 60,
    oldOffsetMinutes: -7 * 60,
    isAlias: true,
  },
  'Canada/Central': {
    release: '2026e',
    newOffsetMinutes: -5 * 60,
    oldOffsetMinutes: -6 * 60,
    isAlias: true,
  },
} as const satisfies Record<string, TzdbZoneExpectation>;

export type TzdbAffectedZone = keyof typeof TZDB_AFFECTED_ZONES;

export const TZDB_AFFECTED_ZONE_NAMES = Object.keys(
  TZDB_AFFECTED_ZONES,
) as TzdbAffectedZone[];

/**
 * `pre-2026b`: no release fully satisfied.
 * `<release>-equivalent`: that release (and all earlier) fully satisfied.
 * `2026e+`: every affected zone has the new rules.
 * `unknown`: the browser could not be probed at all.
 */
type TzdbSummary =
  | 'unknown'
  | 'pre-2026b'
  | `${Exclude<TzdbRelease, '2026e'>}-equivalent`
  | '2026e+';

interface TzdbZoneProbeResult {
  zone: TzdbAffectedZone;
  release: TzdbRelease;
  /** Raw offset label from Intl, e.g. `GMT-07:00`, or null if probing failed */
  observedOffset: string | null;
  observedOffsetMinutes: number | null;
  expectedNewOffset: string;
  expectedOldOffset: string;
  verdict: TzdbVerdict;
}

export interface TzdbProbeResult {
  zones: Record<TzdbAffectedZone, TzdbZoneProbeResult>;
  summary: TzdbSummary;
}

/** Returns the `longOffset` label (e.g. `GMT-07:00`) for a zone at an instant. */
export type TzdbOffsetFormatter = (zone: string, instant: Date) => string;

const defaultOffsetFormatter: TzdbOffsetFormatter = (zone, instant) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'longOffset',
  }).formatToParts(instant);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
};

/** Parses `GMT`, `GMT-7`, `GMT-07:00`, `UTC+05:30` etc. into minutes. */
export const parseGmtOffsetMinutes = (label: string): number | null => {
  const match = label
    .trim()
    .match(/^(?:GMT|UTC)(?:([+\-−])(\d{1,2})(?::?(\d{2}))?)?$/);
  if (!match) {
    return null;
  }
  const [, sign, hours, minutes] = match;
  if (!sign) {
    return 0;
  }
  const total = Number(hours) * 60 + Number(minutes ?? 0);
  return sign === '+' ? total : -total;
};

/** Formats minutes as a `GMT±HH:MM` label matching Intl `longOffset`. */
export const formatGmtOffset = (offsetMinutes: number): string => {
  if (offsetMinutes === 0) {
    return 'GMT';
  }
  const sign = offsetMinutes > 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `GMT${sign}${hh}:${mm}`;
};

const probeZone = (
  zone: TzdbAffectedZone,
  formatter: TzdbOffsetFormatter,
  instant: Date,
): TzdbZoneProbeResult => {
  const expectation: TzdbZoneExpectation = TZDB_AFFECTED_ZONES[zone];
  let observedOffset: string | null = null;
  let observedOffsetMinutes: number | null = null;
  try {
    observedOffset = formatter(zone, instant) || null;
    observedOffsetMinutes = observedOffset
      ? parseGmtOffsetMinutes(observedOffset)
      : null;
  } catch {
    // Unsupported zone or missing Intl support degrades to `unknown`
  }
  let verdict: TzdbVerdict = 'unknown';
  if (observedOffsetMinutes === expectation.newOffsetMinutes) {
    verdict = 'new';
  } else if (observedOffsetMinutes === expectation.oldOffsetMinutes) {
    verdict = 'old';
  }
  return {
    zone,
    release: expectation.release,
    observedOffset,
    observedOffsetMinutes,
    expectedNewOffset: formatGmtOffset(expectation.newOffsetMinutes),
    expectedOldOffset: formatGmtOffset(expectation.oldOffsetMinutes),
    verdict,
  };
};

/** Maps per-zone verdicts to the highest tzdata release fully satisfied. */
const summarizeTzdbVerdicts = (
  zones: Record<TzdbAffectedZone, Pick<TzdbZoneProbeResult, 'verdict'>>,
): TzdbSummary => {
  const canonical = TZDB_AFFECTED_ZONE_NAMES.filter(
    (zone) => !(TZDB_AFFECTED_ZONES[zone] as TzdbZoneExpectation).isAlias,
  );
  if (canonical.every((zone) => zones[zone]?.verdict === 'unknown')) {
    return 'unknown';
  }
  let highest: TzdbRelease | null = null;
  for (const release of TZDB_RELEASES) {
    const satisfied = canonical
      .filter((zone) => TZDB_AFFECTED_ZONES[zone].release === release)
      .every((zone) => zones[zone]?.verdict === 'new');
    if (!satisfied) {
      break;
    }
    highest = release;
  }
  if (!highest) {
    return 'pre-2026b';
  }
  if (highest === '2026e') {
    return '2026e+';
  }
  return `${highest}-equivalent`;
};

/** Probes every affected zone. Never throws. Prefer {@link getTzdbProbeResult}. */
export const probeTzdbRuleset = (
  formatter: TzdbOffsetFormatter = defaultOffsetFormatter,
): TzdbProbeResult => {
  const instant = new Date(TZDB_PROBE_DATE_ISO);
  const zones = {} as Record<TzdbAffectedZone, TzdbZoneProbeResult>;
  for (const zone of TZDB_AFFECTED_ZONE_NAMES) {
    zones[zone] = probeZone(zone, formatter, instant);
  }
  return { zones, summary: summarizeTzdbVerdicts(zones) };
};

let cachedResult: TzdbProbeResult | null = null;

/** Probes once per session and caches the result. */
export const getTzdbProbeResult = (): TzdbProbeResult => {
  if (!cachedResult) {
    cachedResult = probeTzdbRuleset();
  }
  return cachedResult;
};

/** Flat `{ zone: verdict }` map, useful for observability context. */
export const getTzdbVerdictMap = (
  result: TzdbProbeResult,
): Record<TzdbAffectedZone, TzdbVerdict> => {
  const map = {} as Record<TzdbAffectedZone, TzdbVerdict>;
  for (const zone of TZDB_AFFECTED_ZONE_NAMES) {
    map[zone] = result.zones[zone].verdict;
  }
  return map;
};

/** `America/Rainy_River` -> `RainyRiver`, `Canada/Pacific` -> `CanadaPacific` */
export const getTzdbZoneAttributeSuffix = (zone: TzdbAffectedZone): string => {
  const [region, city = ''] = zone.split('/');
  const name = city.replace(/_/g, '');
  return region === 'America' ? name : `${region}${name}`;
};
